import { Model } from '@psyomics/surveyjs-core';
import { GenericError, LoadingSpinner, SurveyJs } from '@psyomics/ui-components';
import React, { memo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import { getConfig } from '../../../config';
import { ReferralRequestSurvey } from '../hooks/use-referral-request-survey';
import { useUpdateReferralRequestSurvey } from '../hooks/use-update-referral-request-survey';

import { reloadPage } from './reload-page';
import { usePrepareSurvey } from './use-prepare-survey';
import { useSurveyHandlers } from './use-survey-handlers';

type SurveyBuilderProps = { readonly survey: ReferralRequestSurvey; readonly reviewMode?: boolean };

const RenderSurvey = memo(({ model }: { readonly model: SurveyJs.Model }) => <SurveyJs.SurveyRenderer model={model} />);

type ErrorTypes = 'SurveyNotFound' | 'SurveyResponseConflict';
const errorMap: Record<ErrorTypes, { titleKey: string; titleComponents?: Record<string, React.ReactElement> }> = {
  SurveyNotFound: { titleKey: 'page.referralRequestSurvey.errors.notFound' },
  SurveyResponseConflict: {
    titleKey: 'page.referralRequestSurvey.errors.conflict',
    titleComponents: {
      refreshLink: (
        <button className="text-blue-600 underline cursor-pointer" onClick={reloadPage} type="button">
          refresh
        </button>
      ),
    },
  },
};

const useSurveyModel = (definition: object, surveyId: string, reviewMode: boolean) => {
  const modelRef = useRef<{ surveyId: string; model: Model } | null>(null);

  if (modelRef.current?.surveyId !== surveyId) {
    const model = new Model(definition);
    if (reviewMode) model.mode = 'display';
    SurveyJs.enforceFileQuestionProperties(model);
    modelRef.current = { surveyId, model };
  }

  return modelRef.current.model;
};

const SurveyBuilder: React.FC<SurveyBuilderProps> = ({ survey, reviewMode = false }) => {
  const navigate = useNavigate();
  const [{ errorMessage }, updateSurveyAnswers] = useUpdateReferralRequestSurvey();
  const surveyModel = useSurveyModel(survey.definition, survey.id, reviewMode);
  const { referralApiUrl } = getConfig();

  const ready = usePrepareSurvey({ model: surveyModel, survey, fetching: false, reviewMode });
  useSurveyHandlers({
    onComplete: () => navigate('/'),
    ready,
    referralApiUrl,
    reviewMode,
    surveyModel,
    surveyResponseId: survey.id,
    surveyResponseUpdatedAt: survey.updatedAt ? new Date(survey.updatedAt) : undefined,
    updateSurveyAnswers,
  });

  if (!ready) return <LoadingSpinner />;
  if (errorMessage && errorMessage !== 'AttachmentValidationFailed')
    return <GenericError {...errorMap[errorMessage]} />;

  return (
    <div className="flex justify-center bg-neutral-100">
      <div className="w-full">
        {errorMessage === 'AttachmentValidationFailed' && (
          <p role="alert">
            One or more attachments couldn&apos;t be verified — please check your uploaded files and try again.
          </p>
        )}
        <RenderSurvey model={surveyModel} />
      </div>
    </div>
  );
};

export { SurveyBuilder };
